import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { toast } from 'sonner';
import { employeesAPI } from '../services/api';
import { KeyRound, UserPlus, Mail, ShieldCheck, ShieldX, RotateCcw, Copy, Send, ShieldAlert, Loader2 } from 'lucide-react';

/**
 * Card shown on the Employee Detail page for HR/Admin to manage the
 * employee's HRMS login account (one-click create, reset, disable, resend welcome).
 */
const EmployeeUserAccountCard = ({ employee, canManage }) => {
  const [account, setAccount] = useState({ has_account: false });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [credsDialog, setCredsDialog] = useState({ open: false, email: '', password: '', mode: '' });

  const fetchAccount = async () => {
    if (!employee?.id) return;
    setLoading(true);
    try {
      const r = await employeesAPI.getUserAccount(employee.id);
      setAccount(r.data || { has_account: false });
    } catch (e) {
      setAccount({ has_account: false });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAccount(); /* eslint-disable-next-line */ }, [employee?.id]);

  const showCreds = (mode, email, password, emailSent) => {
    setCredsDialog({ open: true, email, password, mode, emailSent });
  };

  const copyPassword = () => {
    if (!credsDialog.password) return;
    navigator.clipboard.writeText(credsDialog.password);
    toast.success('Password copied');
  };

  const handleCreate = async () => {
    if (!employee?.email) {
      toast.error('Employee has no email on record');
      return;
    }
    setBusy(true);
    try {
      const r = await employeesAPI.createUserAccount(employee.id, { role: 'employee', force_password_change: true, send_email: true });
      if (r.data.already_exists) {
        toast.info('Account already exists');
      } else {
        toast.success('Login account created');
        showCreds('created', r.data.email, r.data.temp_password, r.data.email_sent);
      }
      fetchAccount();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create account');
    } finally { setBusy(false); }
  };

  const handleReset = async () => {
    if (!window.confirm('Generate a new temporary password? This will force the user to change it on next login.')) return;
    setBusy(true);
    try {
      const r = await employeesAPI.resetUserPassword(employee.id);
      toast.success('Password reset');
      showCreds('reset', account.email, r.data.temp_password, r.data.email_sent);
      fetchAccount();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset failed');
    } finally { setBusy(false); }
  };

  const handleResend = async () => {
    if (!window.confirm('Regenerate temp password and resend the welcome email?')) return;
    setBusy(true);
    try {
      const r = await employeesAPI.resendWelcome(employee.id);
      toast.success('Welcome email resent');
      showCreds('resent', account.email, r.data.temp_password, r.data.email_sent);
      fetchAccount();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Resend failed');
    } finally { setBusy(false); }
  };

  const handleToggle = async () => {
    const activate = !(account.is_active !== false);
    if (!window.confirm(activate ? 'Re-enable this login account?' : 'Disable this login account? The user will not be able to log in.')) return;
    setBusy(true);
    try {
      await employeesAPI.toggleUserAccount(employee.id, activate);
      toast.success(activate ? 'Account enabled' : 'Account disabled');
      fetchAccount();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  if (!canManage) return null;

  return (
    <>
      <Card data-testid="employee-account-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <KeyRound size={18} />
            HRMS Login Account
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 size={14} className="animate-spin" />Checking…</div>
          ) : !account.has_account ? (
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
              <div className="text-sm">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="secondary" data-testid="account-status-badge">No Login</Badge>
                  <span className="text-muted-foreground">Employee cannot access the HRMS.</span>
                </div>
                <p className="text-xs text-muted-foreground">Auto-generates a secure password, emails a welcome link, and forces a password change on first login.</p>
              </div>
              <Button onClick={handleCreate} disabled={busy || !employee?.email} data-testid="create-user-account-btn">
                {busy ? <Loader2 size={14} className="animate-spin mr-2" /> : <UserPlus size={16} className="mr-2" />}
                Create Login Account
              </Button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2">
                  <Badge variant={account.is_active === false ? 'destructive' : 'success'} data-testid="account-status-badge">
                    {account.is_active === false ? 'Disabled' : (account.is_locked ? 'Locked' : 'Active')}
                  </Badge>
                  {account.force_password_change && (
                    <Badge variant="warning" data-testid="account-force-change-badge">Password change pending</Badge>
                  )}
                </div>
                <div><span className="text-muted-foreground">Role:</span> <span className="font-medium capitalize">{(account.role || '').replace('_', ' ')}</span></div>
                <div className="md:col-span-2"><span className="text-muted-foreground">Login email:</span> <span className="font-medium break-all">{account.email}</span></div>
                {account.last_login_at && (
                  <div className="md:col-span-2 text-xs text-muted-foreground">Last login: {new Date(account.last_login_at).toLocaleString()}</div>
                )}
              </div>

              <div className="flex flex-wrap gap-2 pt-2 border-t">
                <Button size="sm" variant="outline" onClick={handleReset} disabled={busy} data-testid="reset-password-btn">
                  <RotateCcw size={14} className="mr-1" />Reset Password
                </Button>
                <Button size="sm" variant="outline" onClick={handleResend} disabled={busy} data-testid="resend-welcome-btn">
                  <Send size={14} className="mr-1" />Resend Welcome
                </Button>
                {account.is_active === false ? (
                  <Button size="sm" variant="outline" onClick={handleToggle} disabled={busy} data-testid="enable-account-btn">
                    <ShieldCheck size={14} className="mr-1" />Enable
                  </Button>
                ) : (
                  <Button size="sm" variant="outline" onClick={handleToggle} disabled={busy} data-testid="disable-account-btn">
                    <ShieldX size={14} className="mr-1" />Disable
                  </Button>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={credsDialog.open} onOpenChange={(o) => setCredsDialog({ ...credsDialog, open: o })}>
        <DialogContent className="sm:max-w-md" data-testid="account-creds-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert size={18} />
              {credsDialog.mode === 'created' ? 'Login Account Created' : credsDialog.mode === 'reset' ? 'Temporary Password Generated' : 'Welcome Email Resent'}
            </DialogTitle>
            <DialogDescription>
              {credsDialog.emailSent
                ? 'The credentials have been emailed to the employee.'
                : 'Email delivery is not configured on this environment — please share these credentials securely with the employee.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <div><span className="text-muted-foreground">Login email:</span> <span className="font-medium break-all">{credsDialog.email}</span></div>
            <div className="flex items-center justify-between gap-2 p-2 bg-muted rounded">
              <div>
                <div className="text-xs text-muted-foreground">Temporary Password</div>
                <div className="font-mono font-semibold" data-testid="temp-password-display">{credsDialog.password}</div>
              </div>
              <Button size="sm" variant="outline" onClick={copyPassword} data-testid="copy-password-btn">
                <Copy size={14} className="mr-1" />Copy
              </Button>
            </div>
            <p className="text-xs text-muted-foreground flex items-center gap-1"><Mail size={12} />Employee will be forced to set a new password on first login.</p>
          </div>
          <DialogFooter>
            <Button onClick={() => setCredsDialog({ ...credsDialog, open: false })} data-testid="creds-close-btn">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EmployeeUserAccountCard;
